import { useQuery, useMutation } from '@apollo/client/react'
import {
  GET_CLIENTS,
  GET_CLIENTS_NAV,
  GET_CLIENT,
  GET_CLIENT_DASHBOARD,
  CREATE_CLIENT,
  UPDATE_CLIENT,
  DELETE_CLIENT,
  SCRAPE_WEBSITE,
  EXTRACT_WEBSITE_FIELDS,
  RECOMMEND_CAMPAIGN_SETTINGS,
} from '@/lib/operations/clients'
import type { ClientConfig, ClientDashboard, ScrapeWebsiteResult, WebsiteSuggestions, CampaignRecommendations } from '@/lib/types'

// Minimal shape returned by GET_CLIENTS_NAV
export interface ClientNavItem {
  id: string
  brandName: string
  agentName?: string | null
  websiteUrl?: string | null
}

// ─── Input types (mirror GQL schema) ─────────────────────────────────────────

export interface CreateClientInput {
  brandName: string
  agentName?: string
  industry?: string
  websiteUrl?: string
  modelProvider?: string
  modelId?: string
  autoRun?: boolean
  autoPublish?: boolean
  selectedContentTypes?: string[]
  [key: string]: unknown
}

export interface MarketingProfileInput {
  phone?: string
  ctaUrl?: string
  campaignGoal?: string
  gbpReviewCount?: number
  gbpRating?: number
  facebookUrl?: string
  linkedinUrl?: string
  twitterUrl?: string
}

export interface CapabilitiesInput {
  businessDescription?: string
  productCategories?: string[]
  servicesOffered?: string[]
  deliveryTimeline?: string
  serviceArea?: string
  acceptedInsurance?: string[]
  ehrIntegrations?: string[]
}

export interface ContentInclusionInput {
  includeAddress?: boolean
  includePhone?: boolean
  includeEmail?: boolean
  includeWebsite?: boolean
  includePricing?: boolean
  includeStaffNames?: boolean
}

export interface AutoGenerateConfigInput {
  enabled: boolean
  contentTypes: string[]
  cadence: 'daily' | 'weekly' | 'custom_cron'
  timeUtc: string
  daysOfWeek?: number[]
  cronExpr?: string
  timezone?: string
}

export interface SchedulingConfigInput {
  autoGenerate?: AutoGenerateConfigInput
  notifyOnQueue?: boolean
}

export interface UpdateClientInput {
  brandName?: string
  brandLocation?: string
  brandSpecialization?: string
  industry?: string
  websiteUrl?: string
  modelProvider?: string
  modelId?: string
  agentName?: string
  autoRun?: boolean
  autoPublish?: boolean
  budgetLimitDailyUsd?: number
  selectedContentTypes?: string[]
  seoKeywords?: string
  compliancePrompt?: string
  marketingProfile?: MarketingProfileInput
  capabilities?: CapabilitiesInput
  contentInclusion?: ContentInclusionInput
  schedulingConfig?: SchedulingConfigInput
  [key: string]: unknown
}

// ─── Read hooks ───────────────────────────────────────────────────────────────

// Lightweight hook for the nav dropdown — only fetches id, brandName, agentName
export function useClientsNav() {
  const { data, loading, error } = useQuery<{ clients: ClientNavItem[] }>(GET_CLIENTS_NAV)
  return { clients: data?.clients ?? [], loading, error }
}

export function useClients() {
  const { data, loading, error } = useQuery<{ clients: ClientConfig[] }>(GET_CLIENTS)
  return { clients: data?.clients ?? [], loading, error }
}

export function useClient(id: string) {
  const { data, loading, error } = useQuery<{ client: ClientConfig }>(GET_CLIENT, {
    variables: { id },
    skip: !id,
  })
  return { client: data?.client, loading, error }
}

export function useClientDashboard(id: string) {
  const { data, loading, error } = useQuery<{ clientDashboard: ClientDashboard }>(GET_CLIENT_DASHBOARD, {
    variables: { id },
    skip: !id,
  })
  return { dashboard: data?.clientDashboard, loading, error }
}

// ─── Mutation hooks ───────────────────────────────────────────────────────────

export function useCreateClient() {
  const [mutate, { loading }] = useMutation<{ createClient: ClientConfig }>(CREATE_CLIENT, {
    refetchQueries: [{ query: GET_CLIENTS }],
  })
  const createClient = (input: CreateClientInput) =>
    mutate({ variables: { input } })
  return { createClient, loading }
}

export function useUpdateClient() {
  const [mutate, { loading }] = useMutation<{ updateClient: ClientConfig }>(UPDATE_CLIENT)
  const updateClient = (id: string, input: UpdateClientInput) =>
    mutate({ variables: { id, input } })
  return { updateClient, loading }
}

export function useDeleteClient() {
  const [mutate, { loading }] = useMutation<{ deleteClient: boolean }>(DELETE_CLIENT, {
    refetchQueries: [{ query: GET_CLIENTS }],
  })
  const deleteClient = (id: string) => mutate({ variables: { id } })
  return { deleteClient, loading }
}

export function useScrapeWebsite() {
  const [mutate, { loading, data }] = useMutation<{ scrapeWebsite: ScrapeWebsiteResult }>(SCRAPE_WEBSITE)
  const scrape = (clientId: string, url: string) =>
    mutate({ variables: { clientId, url } })
  return { scrape, loading, result: data?.scrapeWebsite ?? null }
}

export function useExtractWebsiteFields() {
  const [mutate, { loading }] = useMutation<
    { extractWebsiteFields: WebsiteSuggestions },
    { clientId: string; url: string }
  >(EXTRACT_WEBSITE_FIELDS)
  return {
    extract: (clientId: string, url: string) => mutate({ variables: { clientId, url } }),
    loading,
  }
}

export function useRecommendCampaignSettings() {
  const [mutate, { loading }] = useMutation<
    { recommendCampaignSettings: CampaignRecommendations },
    { clientId: string }
  >(RECOMMEND_CAMPAIGN_SETTINGS)
  return {
    recommend: (clientId: string) => mutate({ variables: { clientId } }),
    loading,
  }
}
